import React, { useEffect, useImperativeHandle, useState } from 'react';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import Drawer from '@app/component/overlays/drawer/Drawer';
import { useQuery } from '@tanstack/react-query';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertExecutionQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import AlertExecution from '@app/model/alert/AlertExecution';
import AlertType from '@app/model/alert/AlertType';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import Utilities from '@util/Utilities';
import PriceComparisonAlertExecutionNotificationOutput
	from '@app/model/alert/output/PriceComparisonAlertExecutionNotificationOutput';
import CompetitorsPriceRateAlertExecutionNotificationOutput
	from '@app/model/alert/output/CompetitorsPriceRateAlertExecutionNotificationOutput';
import ParityPriceComparisonAlertExecutionNotificationOutput
	from '@app/model/alert/output/ParityPriceComparisonAlertExecutionNotificationOutput';
import CompetitorsSoldOutAlertExecutionNotificationOutput
	from '@app/model/alert/output/CompetitorsSoldOutAlertExecutionNotificationOutput';
import CompetitorsPriceRateNotificationRowFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/competitors-price-rate/CompetitorsPriceRateNotificationRowFragment';
import ParityPriceComparisonNotificationRowFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/parity-price-comparison/ParityPriceComparisonNotificationRowFragment';
import CompetitorsSoldOutNotificationRowFragment
	from '@app/site/secured/rateshopper/rates/alert-execution/fragment/competitors-sold-out/CompetitorsSoldOutNotificationRowFragment';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import CompetitorsPriceRateAlertRuleCalculation from '@app/model/alert/CompetitorsPriceRateAlertRuleCalculation';
import ParityPriceComparisonAlertRuleDifferenceType
	from '@app/model/alert/ParityPriceComparisonAlertRuleDifferenceType';
import DataProvider from '@app/model/_common/DataProvider';
import { CompetitorsSoldOutAlertRuleInclusion } from '@app/model/alert/CompetitorsSoldOutAlertRuleInclusion';
import _isEmpty from 'lodash/isEmpty';
import AlertUtilities from '@app/site/secured/rateshopper/rates/alert/util/AlertUtilities';


const MarketStateDayNotificationsDrawer: React.FunctionComponent<{ ref: any }> = React.forwardRef(({}, ref: React.Ref<{
	open: (alertExecutionId: number, statDate: string) => void
}>) => {

	const drawer = useDrawer();
	const [alertExecutionId, setAlertExecutionId] = useState<number>();
	const [statDate, setStatDate] = useState<string>();


	useImperativeHandle(ref, () => {
		return {
			open: (alertExecutionId: number, statDate: string) => {
				setAlertExecutionId(alertExecutionId);
				setStatDate(statDate);
			},
		};
	});

	useEffect(() => {
		if (!!alertExecutionId) drawer.open();
	}, [alertExecutionId]);

	const clear = () => {
		setAlertExecutionId(undefined);
		setStatDate(undefined);
	};


	return <Drawer title="Alert notification" ref={drawer.ref} onClose={clear} size="huge" zIndexClass="z-50">
		{!!alertExecutionId && statDate && <InsideDrawer alertExecutionId={alertExecutionId} statDate={statDate} />}
	</Drawer>;
});

const InsideDrawer: React.FunctionComponent<{ alertExecutionId: number, statDate: string }> = ({ alertExecutionId, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	const { data: alertExecution, isLoading, isSuccess, isError } = useQuery({
		queryFn: () => AlertService.findAlertExecutionById(companyHotel.id, alertExecutionId),
		queryKey: AlertExecutionQueryKeys.detail(alertExecutionId),
	});

	if (isLoading) return <TablePlaceholder />;
	if (isError || !isSuccess) return <Message state="warning">Alert execution could not be found</Message>;

	return <div className="px-4 py-6 sm:px-6 lg:px-8">
		<div className="mx-auto max-w-5xl">
			<AlertConfigurationCard alertExecution={alertExecution} statDate={statDate} />
			<AlertNotificationDetails alertExecution={alertExecution} statDate={statDate} />
		</div>
	</div>;
};

const AlertConfigurationCard: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	const alert = alertExecution.alertOutput.alert;
	const details = getConfigurationDetails(alertExecution);

	return <section className="rounded-xl border border-gray-200 bg-slate-50 p-6 shadow-sm sm:p-8">
		<div className="flex flex-col gap-1">
			<p className="text-xs font-medium uppercase tracking-wide text-gray-400">{Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}</p>
			<h1 className="text-lg font-semibold leading-6 text-gray-950">{alert.name}</h1>
			<p className="text-xs font-medium text-gray-500">Executed at {Utilities.formatDateString(alertExecution.executionDate, 'DD/MM/YYYY HH:mm')}</p>
		</div>

		{!_isEmpty(alertExecution.alertOutput.competitors) && <div className="mt-7">
			<p className="text-sm font-semibold text-gray-900">Competitors</p>
			<div className="mt-3 flex flex-wrap gap-2">
				{alertExecution.alertOutput.competitors.map(c => <span
					key={c.id}
					className="inline-flex items-center rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">{c.name}</span>)}
			</div>
		</div>}

		<div className="mt-7">
			<p className="text-sm font-semibold text-gray-900">Configuration</p>
			<dl className="mt-4 grid grid-cols-1 gap-x-16 gap-y-5 text-sm sm:grid-cols-2">
				{details.map(detail => <div key={detail.label}>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-400">{detail.label}</dt>
					<dd className="mt-1 text-gray-800">{detail.value}</dd>
				</div>)}
			</dl>
		</div>
	</section>;
};

const AlertNotificationDetails: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	switch (alertExecution.alertOutput.alert.type) {
		case AlertType.PRICE_COMPARISON:
			return <PriceComparisonNotificationDetails alertExecution={alertExecution} statDate={statDate} />;
		case AlertType.COMPETITORS_PRICE_RATE:
			return <CompetitorsPriceRateNotificationDetails alertExecution={alertExecution} statDate={statDate} />;
		case AlertType.PARITY_PRICE_COMPARISON:
			return <ParityPriceComparisonNotificationDetails alertExecution={alertExecution} statDate={statDate} />;
		case AlertType.COMPETITORS_SOLD_OUT:
			return <CompetitorsSoldOutNotificationDetails alertExecution={alertExecution} statDate={statDate} />;
		default:
			return <Message state="warning">Alert type is not supported in this drawer</Message>;
	}
};

const PriceComparisonNotificationDetails: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: notificationOutputs, isLoading, isSuccess, isError } = useQuery({
		queryFn: () => AlertService.findPriceComparisonAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});
	const notificationOutput = findPriceComparisonNotification(notificationOutputs, statDate);

	if (isLoading) return <TablePlaceholder />;
	if (isError) return <Message state="warning">Notification details could not be found</Message>;
	if (!isSuccess || !notificationOutput) return <NoNotificationForDate statDate={statDate} />;

	return <section className="mt-8 rounded-xl border border-gray-200 bg-slate-50 p-6 shadow-sm sm:p-8">
		<h2 className="text-base font-semibold text-gray-950">Notification Details</h2>
		<div className="mt-5 overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead>
				<tr>
					<th className="py-3 pl-0 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Date</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Your Price</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Competitors Price</th>
					{alertExecution.alertOutput.priceComparisonAlertRule.pricePoint !== PriceComparisonAlertRulePricePoint.MEDIAN &&
						<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Competitor</th>}
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Difference</th>
				</tr>
				</thead>
				<tbody className="divide-y divide-gray-200">
				<PriceComparisonDrawerNotificationRow
					alertExecution={alertExecution}
					notificationOutput={notificationOutput} />
				</tbody>
			</table>
		</div>
	</section>;
};

const PriceComparisonDrawerNotificationRow: React.FunctionComponent<{
	alertExecution: AlertExecution,
	notificationOutput: PriceComparisonAlertExecutionNotificationOutput
}> = ({ alertExecution, notificationOutput }) => {
	const findCompetitor = (competitorId: number) => {
		return alertExecution.alertOutput.competitors!.find(c => c.id === competitorId);
	};

	return <tr>
		<td className="whitespace-nowrap py-4 pl-0 pr-3 text-sm font-semibold text-gray-950">
			{Utilities.formatDateString(notificationOutput.current.statDate, 'ddd DD MMM', false)}
		</td>
		<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
			{Utilities.formatDouble(notificationOutput.current.price.amount, 0)}
		</td>
		<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
			{Utilities.formatDouble(notificationOutput.current.competitorsPrice.amount, 0)}
		</td>
		{alertExecution.alertOutput.priceComparisonAlertRule.pricePoint !== PriceComparisonAlertRulePricePoint.MEDIAN &&
			<td className="whitespace-nowrap px-2 py-4 text-sm text-gray-600">
				{findCompetitor(notificationOutput.current.competitorId)?.name}
			</td>}
		<td className="whitespace-nowrap px-2 py-4 text-sm">
			<FormatPriceComparisonDifferencePercentage percentage={AlertUtilities.calculateDifference(notificationOutput.current)} />
		</td>
	</tr>;
};

const FormatPriceComparisonDifferencePercentage: React.FunctionComponent<{ percentage: number }> = ({ percentage }) => {
	const rounded = parseInt(Utilities.formatDouble(percentage, 0));
	return rounded <= 0
		? <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/10">{rounded}%</span>
		: <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/10">+{rounded}%</span>;
};

const CompetitorsPriceRateNotificationDetails: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: notificationOutputs, isLoading, isSuccess, isError } = useQuery({
		queryFn: () => AlertService.findCompetitorsPriceRateAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});
	const notificationOutput = findCompetitorsPriceRateNotification(notificationOutputs, statDate);

	if (isLoading) return <TablePlaceholder />;
	if (isError) return <Message state="warning">Notification details could not be found</Message>;
	if (!isSuccess || !notificationOutput) return <NoNotificationForDate statDate={statDate} />;

	return <section className="mt-8 rounded-xl border border-gray-200 bg-slate-50 p-6 shadow-sm sm:p-8">
		<h2 className="text-base font-semibold text-gray-950">Notification Details</h2>
		<div className="mt-5 overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead>
				<tr>
					<th className="py-3 pl-0 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Competitor</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Room Type</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Price</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Room Found</th>
					<th className="py-3 pl-3 pr-0 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Change</th>
				</tr>
				</thead>
				<CompetitorsPriceRateNotificationRowFragment
					companyHotel={companyHotel}
					alertExecution={alertExecution}
					notificationOutput={notificationOutput} />
			</table>
		</div>
	</section>;
};

const ParityPriceComparisonNotificationDetails: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: notificationOutputs, isLoading, isSuccess, isError } = useQuery({
		queryFn: () => AlertService.findParityPriceComparisonAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});
	const notificationOutput = findParityPriceComparisonNotification(notificationOutputs, statDate);

	if (isLoading) return <TablePlaceholder />;
	if (isError) return <Message state="warning">Notification details could not be found</Message>;
	if (!isSuccess || !notificationOutput) return <NoNotificationForDate statDate={statDate} />;

	return <section className="mt-8 rounded-xl border border-gray-200 bg-slate-50 p-6 shadow-sm sm:p-8">
		<h2 className="mb-5 text-base font-semibold text-gray-950">Notification Details</h2>
		<ParityPriceComparisonNotificationRowFragment
			companyHotel={companyHotel}
			alertExecution={alertExecution}
			notificationOutput={notificationOutput} />
	</section>;
};

const CompetitorsSoldOutNotificationDetails: React.FunctionComponent<{ alertExecution: AlertExecution, statDate: string }> = ({ alertExecution, statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: notificationOutputs, isLoading, isSuccess, isError } = useQuery({
		queryFn: () => AlertService.findCompetitorsSoldOutAlertExecutionNotifications(companyHotel.id, alertExecution.id),
		queryKey: AlertExecutionQueryKeys.notifications(alertExecution.id),
	});
	const notificationOutput = findCompetitorsSoldOutNotification(notificationOutputs, statDate);

	if (isLoading) return <TablePlaceholder />;
	if (isError) return <Message state="warning">Notification details could not be found</Message>;
	if (!isSuccess || !notificationOutput) return <NoNotificationForDate statDate={statDate} />;

	return <section className="mt-8 rounded-xl border border-gray-200 bg-slate-50 p-6 shadow-sm sm:p-8">
		<h2 className="text-base font-semibold text-gray-950">Notification Details</h2>
		<div className="mt-5 overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead>
				<tr>
					<th className="py-3 pl-0 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Date</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Sold Out</th>
					<th className="px-2 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">Competitors</th>
					<th className="py-3 pl-3 pr-0 text-right text-xs font-semibold uppercase tracking-wide text-gray-400">State</th>
				</tr>
				</thead>
				<tbody className="divide-y divide-gray-200">
				<CompetitorsSoldOutNotificationRowFragment
					companyHotel={companyHotel}
					notification={notificationOutput.current} />
				</tbody>
			</table>
		</div>
	</section>;
};

const NoNotificationForDate: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	return <div className="mt-8">
		<Message state="warning">No notification details found for {Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}</Message>
	</div>;
};

const getConfigurationDetails = (alertExecution: AlertExecution) => {
	const alert = alertExecution.alertOutput.alert;
	const formatDays = (days: number) => `${days} ${days === 1 ? 'Day' : 'Days'}`;
	const baseDetails = [
		{ label: 'Type', value: Utilities.snakeCaseToWords(alert.type) },
		{ label: 'Dates', value: `Next ${formatDays(alert.timeframe.days)}` },
	];

	switch (alert.type) {
		case AlertType.PRICE_COMPARISON: {
			const rule = alertExecution.alertOutput.priceComparisonAlertRule;
			return [
				...baseDetails,
				{ label: 'Price Calculation', value: formatPricePoint(rule.pricePoint) },
				{ label: 'Range', value: `${rule.priceRange.from}% to ${rule.priceRange.to}%` },
			];
		}
		case AlertType.COMPETITORS_PRICE_RATE: {
			const rule = alertExecution.alertOutput.competitorsPriceRateAlertRule;
			return [
				...baseDetails,
				{ label: 'Calculation', value: formatCompetitorsPriceRateCalculation(rule.calculation) },
				{ label: 'Monitoring', value: `Last ${formatDays(rule.pastPeriodInDays)}` },
				{ label: 'Trigger', value: `${rule.priceRatePercentage}%` },
			];
		}
		case AlertType.PARITY_PRICE_COMPARISON: {
			const rule = alertExecution.alertOutput.parityPriceComparisonAlertRule;
			return [
				...baseDetails,
				{ label: 'Trigger Difference', value: formatParityTriggerDifference(rule.triggerDifference, rule.differenceType) },
				{ label: 'Providers', value: rule.providers.filter(provider => provider !== DataProvider.BRAND_COM).map(Utilities.snakeCaseToWords).join(', ') },
			];
		}
		case AlertType.COMPETITORS_SOLD_OUT: {
			const rule = alertExecution.alertOutput.competitorsSoldOutAlertRule;
			return [
				...baseDetails,
				{ label: 'Include Competitors', value: formatCompetitorsSoldOutInclusion(rule.competitorsInclusion, rule.inclusionPercentage) },
			];
		}
		default:
			return baseDetails;
	}
};

const findPriceComparisonNotification = (notificationOutputs: PriceComparisonAlertExecutionNotificationOutput[] | undefined, statDate: string) => {
	return notificationOutputs?.find(notificationOutput => isSameStatDate(notificationOutput.current.statDate, statDate));
};

const findCompetitorsPriceRateNotification = (notificationOutputs: CompetitorsPriceRateAlertExecutionNotificationOutput[] | undefined, statDate: string) => {
	return notificationOutputs?.find(notificationOutput => isSameStatDate(notificationOutput.statDate, statDate));
};

const findParityPriceComparisonNotification = (notificationOutputs: ParityPriceComparisonAlertExecutionNotificationOutput[] | undefined, statDate: string) => {
	return notificationOutputs?.find(notificationOutput => isSameStatDate(notificationOutput.current.statDate, statDate));
};

const findCompetitorsSoldOutNotification = (notificationOutputs: CompetitorsSoldOutAlertExecutionNotificationOutput[] | undefined, statDate: string) => {
	return notificationOutputs?.find(notificationOutput => isSameStatDate(notificationOutput.current.statDate, statDate));
};

const isSameStatDate = (left: string | undefined, right: string | undefined) => {
	return left?.substring(0, 10) === right?.substring(0, 10);
};

const formatPricePoint = (pricePoint: PriceComparisonAlertRulePricePoint) => {
	switch (pricePoint) {
		case PriceComparisonAlertRulePricePoint.MEDIAN:
			return 'Median';
		case PriceComparisonAlertRulePricePoint.MIN:
			return 'Least Expensive';
		case PriceComparisonAlertRulePricePoint.MAX:
			return 'Most Expensive';
	}
};

const formatCompetitorsPriceRateCalculation = (calculation: CompetitorsPriceRateAlertRuleCalculation) => {
	switch (calculation) {
		case CompetitorsPriceRateAlertRuleCalculation.INCREASE:
			return 'More than';
		case CompetitorsPriceRateAlertRuleCalculation.DECREASE:
			return 'Less than';
		case CompetitorsPriceRateAlertRuleCalculation.INCREASE_AND_DECREASE:
			return 'More or less than';
	}
};

const formatParityTriggerDifference = (triggerDifference: number, differenceType: ParityPriceComparisonAlertRuleDifferenceType) => {
	return differenceType === ParityPriceComparisonAlertRuleDifferenceType.PERCENTAGE
		? `${triggerDifference}%`
		: `${triggerDifference}`;
};

const formatCompetitorsSoldOutInclusion = (inclusion: CompetitorsSoldOutAlertRuleInclusion, inclusionPercentage: number) => {
	return inclusion === CompetitorsSoldOutAlertRuleInclusion.SOME
		? `${inclusionPercentage}%`
		: Utilities.snakeCaseToWords(inclusion);
};

export default MarketStateDayNotificationsDrawer;
